import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeOverlapTarget, SEVERITY_NOTE } from '../../utils/classOverlapFix';
import { buildRosterOverlap, summarizeRosterOverlap } from '../../utils/rosterClassOverlap';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import '../../components/chat/batch/ClassCheckPanel.css';

/**
 * 학생별 기록의 '반 전체 점검'. 왓퀴즈에서 쓴 생기부(다중·단일)를 영역별로 맞대어 같은/거의 같은 문장을 찾는다.
 * 누를 때만 펼친다(계산은 브라우저 안에서 바로 끝난다).
 */
const RosterClassCheck = ({ rosterId, students = [], classPath }) => {
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => buildRosterOverlap(students), [students]);
  const summary = useMemo(() => summarizeRosterOverlap(groups), [groups]);
  if (groups.length === 0) return null;

  const toggle = () => {
    if (!open) {
      trackClassRosterEvent('class_overlap_check', {
        roster_id: rosterId,
        student_count: students.length,
        group_count: summary.groupCount,
        flagged_count: summary.flaggedStudents,
        same_count: summary.sameCount,
        near_count: summary.nearCount,
        max_people: summary.maxPeople,
        level: summary.level,
      });
    }
    setOpen((value) => !value);
  };

  return (
    <section className="roster-class-check" data-testid="roster-class-check">
      <button type="button" className="roster-class-check-toggle" aria-expanded={open} onClick={toggle}>
        <span>
          <strong>반 전체 점검</strong>
          <span>왓퀴즈에서 쓴 생기부 {summary.groupCount}개 영역을 학생끼리 맞대어 봐요</span>
        </span>
        <span className="roster-class-check-chevron" aria-hidden="true">{open ? '접기' : '보기'}</span>
      </button>
      {open && (
        <div className="roster-class-check-body">
          {groups.map((group) => {
            const { plan } = group;
            const note = SEVERITY_NOTE[plan.summary.severity];
            if (plan.targets.length === 0) {
              return (
                <div key={group.key} className="class-check is-clean">
                  <strong>{group.label} · {group.entries.length}명</strong>
                  <span>학생마다 다른 문장으로 써졌어요.</span>
                </div>
              );
            }
            return (
              <div key={group.key} className="class-check">
                <div className="class-check-title">
                  <strong>{group.label} · {group.entries.length}명</strong>
                  <span>
                    {plan.summary.sameGroups > 0 && `같은 문장 ${plan.summary.sameGroups}`}
                    {plan.summary.sameGroups > 0 && plan.summary.nearGroups > 0 && ' · '}
                    {plan.summary.nearGroups > 0 && `거의 같은 문장 ${plan.summary.nearGroups}`}
                    {` · ${plan.targets.length}명만 고치면 돼요`}
                  </span>
                </div>
                {note && <p className="class-check-note">{note}</p>}
                <ul className="class-check-list">
                  {plan.targets.map((target) => {
                    const entry = group.entries[target.index];
                    return (
                      <li key={entry.sid}>
                        <div className="class-check-student">
                          <span className="class-check-name">{entry.name}</span>
                          <span className="class-check-reason">{describeOverlapTarget(target)}</span>
                        </div>
                        <Link className="roster-class-check-open" to={`${classPath}/students/${encodeURIComponent(entry.sid)}`}>
                          보기
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <p className="roster-class-check-note">
            학생마다 영역별 가장 최근 글로 점검해요. 나이스에서 고친 최종본은 생기부 검사기에 넣어 점검하세요.
          </p>
        </div>
      )}
    </section>
  );
};

export default RosterClassCheck;
